/**
 * CT: Seamless, scroll-direction-controlled ticker
 * - Repeats content by "unit width" so you never see the end of the cycle.
 * - Direction follows scroll: down => right->left (-1), up => left->right (+1).
 */

(function () {
  const ROOT_SEL = '.ct__container';
  const TRACK_SEL = '.ct__track';
  const ITEM_SEL = '.ct__item';

  /* Scroll → direction */
  let lastY = window.scrollY;
  function setDirFromScroll() {
    const y = window.scrollY;
    const d = y - lastY;
    if (d !== 0) {
      const dir = d > 0 ? -1 : 1; // down = -1 (R→L), up = +1 (L→R)
      document.querySelectorAll(ROOT_SEL).forEach((el) => {
        el.style.setProperty('--ct-direction', dir);
      });
    }
    lastY = y;
  }
  addEventListener('scroll', setDirFromScroll, { passive: true });
  addEventListener(
    'wheel',
    (e) => {
      if (e.deltaY !== 0) {
        const dir = e.deltaY > 0 ? -1 : 1;
        document
          .querySelectorAll(ROOT_SEL)
          .forEach((el) => el.style.setProperty('--ct-direction', dir));
      }
    },
    { passive: true },
  );

  class CT {
    constructor(root) {
      this.root = root;
      this.track = root.querySelector(TRACK_SEL);
      this.base = this.track.querySelector(ITEM_SEL); // single base sequence
      this.offset = 0;
      this.lastTs = null;
      this.reduced = matchMedia('(prefers-reduced-motion: reduce)').matches;

      this.buildOnce();
      this.ro = new ResizeObserver(() => this.remeasureAndFill());
      this.ro.observe(this.root);

      matchMedia('(prefers-reduced-motion: reduce)').addEventListener('change', (e) => {
        this.reduced = e.matches;
      });

      requestAnimationFrame(this.loop);
    }

    get speed() {
      const v = parseFloat(getComputedStyle(this.root).getPropertyValue('--ct-speed'));
      return Number.isNaN(v) ? 30 : Math.max(0, Math.min(v, 2000));
    }
    get direction() {
      const v = parseFloat(getComputedStyle(this.root).getPropertyValue('--ct-direction'));
      return v >= 0 ? 1 : -1;
    }

    buildOnce() {
      if (!this.base) return;
      // Start with exactly one base element in the track
      this.track.innerHTML = '';
      this.track.appendChild(this.base);

      this.remeasureAndFill();
      this.offset = -0.01; // tiny negative to avoid exact 0 seam on first paint
      this.apply();
    }

    remeasureAndFill() {
      // Measure width of the *base* sequence
      this.seqWidth = this.base.getBoundingClientRect().width;

      // If not laid out yet, force one more frame by cloning once
      if (this.seqWidth === 0) {
        const t = this.base.cloneNode(true);
        this.track.appendChild(t);
        this.seqWidth = this.base.getBoundingClientRect().width || t.getBoundingClientRect().width;
      }

      // Ensure total content width >= container + seqWidth
      // (so there is always at least one full sequence ahead of the modulo wrap)
      const need = this.root.getBoundingClientRect().width + this.seqWidth;

      // Remove any old clones, keep the first as base
      const kids = Array.from(this.track.children);
      for (let i = kids.length - 1; i >= 1; i--) this.track.removeChild(kids[i]);

      // Clone base until we surpass the need
      let total = this.base.getBoundingClientRect().width;
      while (total < need) {
        const c = this.base.cloneNode(true);
        this.track.appendChild(c);
        total += this.seqWidth; // identical content → identical width
      }

      // Precompute modulo bound
      this.mod = this.seqWidth;
    }

    apply() {
      // Use modulo to guarantee *continuous* motion with no end frame
      // Keep offset in [-mod, 0)
      let o = this.offset % this.mod;
      if (o > 0) o -= this.mod;

      // Limit float jitter (round to 1/100 px to avoid micro-blinks on some GPUs)
      const snap = Math.round(o * 100) / 100;
      this.track.style.transform = `translate3d(${snap}px, 0, 0)`;
    }

    loop = (ts) => {
      if (this.lastTs == null) this.lastTs = ts;
      const dt = (ts - this.lastTs) / 1000;
      this.lastTs = ts;

      const v = this.reduced ? 0 : this.speed;
      this.offset += v * this.direction * dt;

      // No DOM writes, only modulo transform
      this.apply();
      requestAnimationFrame(this.loop);
    };
  }

  document.querySelectorAll(ROOT_SEL).forEach((el) => new CT(el));
})();
